export type HomepageVoucherPlacement = 'banner' | 'float';

export interface VoucherFeatureSettings {
  enabled: boolean;
  allowGuestApply: boolean;
  /** Show the active voucher on the public homepage. */
  showHomepageFloat: boolean;
  /** Banner sits under the header; float is the corner overlay. */
  homepagePlacement: HomepageVoucherPlacement;
}

export const DEFAULT_VOUCHER_FEATURE_SETTINGS: VoucherFeatureSettings = {
  enabled: true,
  allowGuestApply: true,
  showHomepageFloat: true,
  homepagePlacement: 'banner',
};

export interface VoucherPlatformDefaults {
  enabled: boolean;
  allowGuest: boolean;
  showHomepageFloat: boolean;
  homepagePlacement: HomepageVoucherPlacement;
}

export const DEFAULT_VOUCHER_PLATFORM_DEFAULTS: VoucherPlatformDefaults = {
  enabled: true,
  allowGuest: true,
  showHomepageFloat: true,
  homepagePlacement: 'banner',
};

function asBool(raw: unknown, fallback: boolean): boolean {
  if (raw === false || raw === 'false') return false;
  if (raw === true || raw === 'true') return true;
  return fallback;
}

export function parseHomepageVoucherPlacement(raw: unknown): HomepageVoucherPlacement {
  const value = String(raw ?? '')
    .trim()
    .toLowerCase();
  return value === 'float' ? 'float' : 'banner';
}

export function parseVoucherPlatformDefaults(raw: unknown): VoucherPlatformDefaults {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_VOUCHER_PLATFORM_DEFAULTS };
  const o = raw as Record<string, unknown>;
  const nested = (o.vouchers as Record<string, unknown> | undefined) ?? o;
  return {
    enabled: asBool(nested.enabled, DEFAULT_VOUCHER_PLATFORM_DEFAULTS.enabled),
    allowGuest: asBool(nested.allow_guest ?? nested.allowGuest, DEFAULT_VOUCHER_PLATFORM_DEFAULTS.allowGuest),
    showHomepageFloat: asBool(
      nested.show_homepage_float ?? nested.showHomepageFloat,
      DEFAULT_VOUCHER_PLATFORM_DEFAULTS.showHomepageFloat,
    ),
    homepagePlacement: parseHomepageVoucherPlacement(nested.homepage_placement ?? nested.homepagePlacement),
  };
}

export function parseVoucherFeatureSettings(
  orgSettings?: Record<string, unknown> | null,
  platform?: VoucherPlatformDefaults | null,
): VoucherFeatureSettings {
  const platformDefaults = platform ?? DEFAULT_VOUCHER_PLATFORM_DEFAULTS;
  const raw = (orgSettings?.vouchers as Record<string, unknown> | undefined) ?? {};
  const enabled = raw.enabled === false ? false : platformDefaults.enabled && raw.enabled !== false;
  const allowGuestApply =
    raw.allow_guest === false || raw.allowGuest === false
      ? false
      : platformDefaults.allowGuest && raw.allow_guest !== false && raw.allowGuest !== false;
  const showHomepageFloat =
    raw.show_homepage_float === false || raw.showHomepageFloat === false
      ? false
      : platformDefaults.showHomepageFloat &&
        raw.show_homepage_float !== false &&
        raw.showHomepageFloat !== false;
  const homepagePlacement = parseHomepageVoucherPlacement(
    raw.homepage_placement ?? raw.homepagePlacement ?? platformDefaults.homepagePlacement,
  );
  return { enabled, allowGuestApply, showHomepageFloat, homepagePlacement };
}

export function serializeVoucherOrgSettings(
  settings: VoucherFeatureSettings,
): Record<string, string | boolean> {
  return {
    enabled: settings.enabled,
    allow_guest: settings.allowGuestApply,
    show_homepage_float: settings.showHomepageFloat,
    homepage_placement: settings.homepagePlacement,
  };
}

export function serializeVoucherPlatformDefaults(
  settings: VoucherPlatformDefaults,
): Record<string, string | boolean> {
  return {
    enabled: settings.enabled,
    allow_guest: settings.allowGuest,
    show_homepage_float: settings.showHomepageFloat,
    homepage_placement: settings.homepagePlacement,
  };
}

export function voucherApplyAllowed(settings: VoucherFeatureSettings, isAuthenticated: boolean): boolean {
  if (!settings.enabled) return false;
  if (isAuthenticated) return true;
  return settings.allowGuestApply;
}
